import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Star, Package, Clock, Wrench, Zap, Phone, CheckCircle2, RotateCcw, ChevronRight, ChevronLeft, ChevronDown, ArrowRight, ArrowDown, Info, MapPin, Shield, Mail, Send, Check, AlertCircle, Bot, Sparkles, PartyPopper, Ban, TrendingUp, Copy } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  SocialProofTicker, TrustStrip, PriceLockBadge,
  EmailOfferButton, SmsOfferButton, ExitIntentModal,
} from './sell/ConversionEnhancements';
import SmsOptInCheckbox, { SMS_OPT_IN_CONSENT_TEXT } from '../shared/SmsOptInCheckbox';
import OrderFaq from './OrderFaq';
import { formatPhoneInput, isEmailValid, isPhoneValid, isZipValid, FIELD_PROPS, ReassuranceStrip, StepProgress, FieldStatus, SuggestionDropdown, SpecPicker, SpecLoading } from '../shared/EstimateEnhancements';
import { useDeviceSuggestions, useModelSpecs, useAiModelCandidates, buildSpecQuery, parseQualifiers } from '../shared/useDeviceSuggestions';
import { GuideProgress, GuideCoach, GuidePointer, DevicePicker, GuideStyles } from './sell/SellGuideLayer';
import { getDeviceImage } from '../shared/deviceImages';
import { buildSellSpecQuery } from '../shared/instantSellSpecs';
import { SellContactSupport } from './sell/SellContactSupport';
import { PhonePriceNote } from './sell/PhonePriceNote';
import { EstimateContactBar } from './sell/EstimateContactBar';

const API_URL = (() => { const envUrl = process.env.REACT_APP_BACKEND_URL; if (!envUrl || envUrl === '') return window.location.origin; try { const envHost = new URL(envUrl).hostname; const currentHost = window.location.hostname; if (currentHost === 'localhost' || currentHost === '127.0.0.1' || envHost === currentHost || currentHost.includes('preview.emergentagent.com')) { return envUrl; } return window.location.origin; } catch (e) { return window.location.origin; } })();

const LOADING_STEPS = [
  'Initializing market scanner...',
  'Scanning live market data...',
  'Analyzing pricing trends...',
  'Cross-referencing databases...',
  'Running pricing algorithms...',
  'Verifying accuracy...',
  'Generating your offer...',
];

const SPEC_REQUIREMENTS = {
  phone: [
    { label: 'Model', hint: 'e.g. iPhone 16 Pro', regex: /iphone\s?\d|galaxy\s?s\s?\d{2,}|pixel\s?\d|oneplus|motorola|razr|galaxy\s?z\s?(flip|fold)/i },
    { label: 'Storage', hint: 'e.g. 256GB', regex: /\d+\s?gb|\d+\s?tb/i },
    { label: 'Carrier', hint: 'e.g. Unlocked', regex: /unlocked|at&t|att|verizon|t-mobile|tmobile|sprint|carrier/i },
  ],
  laptop: [
    { label: 'Model', hint: 'e.g. MacBook Pro 14', regex: /macbook|thinkpad|xps|spectre|surface|chromebook|dell|hp|lenovo/i },
    { label: 'RAM', hint: 'e.g. 16GB', regex: /\d+\s?gb\s?ram|\d+gb\s+\d+gb|ram/i },
    { label: 'Storage', hint: 'e.g. 512GB SSD', regex: /\d+\s?gb\s?ssd|\d+\s?tb|ssd|storage/i },
    { label: 'Processor/Year', hint: 'e.g. M3 Pro', regex: /m[1-4]|i[3579]|ryzen|intel|amd|20[12]\d/i },
  ],
  tablet: [
    { label: 'Model', hint: 'e.g. iPad Pro 12.9', regex: /ipad|galaxy\s?tab|surface\s?pro|fire\s?hd/i },
    { label: 'Storage', hint: 'e.g. 256GB', regex: /\d+\s?gb|\d+\s?tb/i },
    { label: 'Connectivity', hint: 'WiFi or Cellular', regex: /wifi|cellular|wi-fi|lte|5g/i },
  ],
  watch: [
    { label: 'Model', hint: 'e.g. Apple Watch Ultra 2', regex: /apple\s?watch|galaxy\s?watch|pixel\s?watch|garmin|fitbit/i },
    { label: 'Size', hint: 'e.g. 45mm', regex: /\d+\s?mm/i },
    { label: 'Type', hint: 'GPS or Cellular', regex: /gps|cellular|bluetooth|lte/i },
  ],
  console: [
    { label: 'Model', hint: 'e.g. PS5 Disc', regex: /ps[4-9]|playstation\s?[4-9]|xbox|nintendo|switch|steam\s?deck/i },
    { label: 'Edition', hint: 'Disc or Digital', regex: /disc|digital|oled|lite|slim|pro|standard/i },
    { label: 'Storage', hint: 'e.g. 1TB', regex: /\d+\s?gb|\d+\s?tb/i },
  ],
  glasses: [
    { label: 'Model', hint: 'e.g. Meta Quest 3', regex: /meta\s?quest|quest\s?\d|ray.?ban\s?meta|oakley\s?meta|meta\s?glasses|orion\s?glasses|vision\s?pro/i },
  ],
  meta: [
    { label: 'Model', hint: 'e.g. Meta Quest 3', regex: /meta\s?quest|quest\s?\d|oculus|ray.?ban\s?meta|oakley\s?meta|meta\s?glasses|ray.?ban\s?stories/i },
    { label: 'Storage', hint: 'e.g. 256GB (Quest only)', regex: /\d+\s?gb|\d+\s?tb/i },
  ],
  accessory: [
    { label: 'Model', hint: 'e.g. AirPods Pro 2', regex: /airpods|homepod|apple\s?tv|imac|mac\s?mini|mac\s?studio|mac\s?pro/i },
  ],
};

const CONDITIONS = [
  {
    id: 'flawless', label: 'New / Open Box', Icon: Star, color: '#10b981',
    image: '/images/conditions/flawless.webp',
    highlights: [
      'Brand new sealed or open box, never used',
      'No scratches, dents, or visible wear anywhere on the body or frame',
      'Screen is crystal clear with zero blemishes, scratches, or marks',
      'All buttons, ports, and features function perfectly',
      'Battery health is excellent (90%+ for phones)',
    ],
    tip: 'This is the highest grade. Your device should be brand new or open box with absolutely no signs of use.',
  },
  {
    id: 'good', label: 'Good', Icon: Package, color: '#3b82f6',
    image: '/images/conditions/poor.webp',
    highlights: [
      "Light scratches or scuffs on body/frame not visible at arm's length",
      'Screen may have very faint hairline scratches only visible under direct light',
      'No dents, dings, or deep scratches on body or screen',
      'All functions work perfectly, no glitches or issues',
      'Battery health/HDMI/USB/Type-C ports all work fine',
    ],
    tip: 'The same scratches criteria applies to the screen, only very faint marks that disappear when the screen is on.',
  },
  {
    id: 'poor', label: 'Poor', Icon: Clock, color: '#f59e0b',
    image: '/images/conditions/good.webp',
    highlights: [
      'Excessive scratching visible on body, frame, and/or screen',
      'Major dents, dings, or scuffs on the device body',
      'Screen may have deep scratches visible when display is on',
      'Housing damage like a slightly bent frame',
      'Device still powers on and core functions work',
    ],
    tip: 'Deep scratches or heavy scuffing that is clearly visible counts as Poor. The key difference from "Broken" is no cracks.',
  },
  {
    id: 'broken', label: 'Broken', Icon: Wrench, color: '#ef4444',
    image: '/images/conditions/broken.webp',
    highlights: [
      'Cracked screen of any size, even a small crack counts',
      'Broken or shattered back panel or body',
      'Physical damage to ports, buttons, or camera',
      'Water damage indicators triggered',
      'Device may or may not power on',
    ],
    tip: 'Any crack on the screen or body, regardless of how small, means the device is in "Broken" condition.',
  },
];

const PAYMENT_METHODS = [
  { id: 'cashapp', name: 'Cash App', placeholder: '$cashtag' },
  { id: 'zelle', name: 'Zelle', placeholder: 'Email or phone' },
  { id: 'paypal', name: 'PayPal', placeholder: 'PayPal email' },
  { id: 'venmo', name: 'Venmo', placeholder: '@username' },
];

// ─── Robot Face SVG ───
function RobotFace({ scanning, size = 80 }) {
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size} className="drop-shadow-[0_0_20px_rgba(6,182,212,0.4)]">
        <line x1="50" y1="8" x2="50" y2="18" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
        <circle cx="50" cy="6" r="4" fill="#06b6d4" className={scanning ? 'animate-pulse' : ''}>
          <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <rect x="15" y="18" width="70" height="62" rx="16" fill="#0f172a" stroke="#06b6d4" strokeWidth="2" />
        <rect x="15" y="18" width="70" height="62" rx="16" fill="url(#robotGrad)" opacity="0.15" />
        <rect x="25" y="32" width="20" height="14" rx="5" fill="#0c1220" stroke="#06b6d4" strokeWidth="1.5" />
        <rect x="55" y="32" width="20" height="14" rx="5" fill="#0c1220" stroke="#06b6d4" strokeWidth="1.5" />
        <circle cx="35" cy="39" r="4" fill="#06b6d4">
          {scanning && <animate attributeName="cx" values="32;38;32" dur="1.2s" repeatCount="indefinite" />}
        </circle>
        <circle cx="65" cy="39" r="4" fill="#06b6d4">
          {scanning && <animate attributeName="cx" values="62;68;62" dur="1.2s" repeatCount="indefinite" />}
        </circle>
        <rect x="32" y="56" width="36" height="10" rx="3" fill="#0c1220" stroke="#06b6d4" strokeWidth="1" />
        {[0,1,2,3,4].map(i => (
          <line key={i} x1={39 + i * 7} y1="57" x2={39 + i * 7} y2="65" stroke="#06b6d4" strokeWidth="0.8" opacity={scanning ? "1" : "0.4"}>
            {scanning && <animate attributeName="opacity" values="0.4;1;0.4" dur="0.5s" begin={`${i * 0.1}s`} repeatCount="indefinite" />}
          </line>
        ))}
        <circle cx="12" cy="45" r="5" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
        <circle cx="88" cy="45" r="5" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
        {scanning && (
          <rect x="17" y="30" width="66" height="2" fill="#06b6d4" opacity="0.6" rx="1">
            <animate attributeName="y" values="30;68;30" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
          </rect>
        )}
        <defs>
          <linearGradient id="robotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// ─── Confetti Component ───
function Confetti() {
  const colors = ['#06b6d4', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444'];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
      {Array.from({ length: 50 }).map((_, i) => (
        <div
          key={i}
          className="absolute ie-confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-10px',
            width: `${6 + Math.random() * 6}px`,
            height: `${6 + Math.random() * 6}px`,
            backgroundColor: colors[i % colors.length],
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animationDelay: `${Math.random() * 1.5}s`,
            animationDuration: `${2 + Math.random() * 2}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function InstantEstimate({ calculateApiOverride, extraPayload, internalMode = false } = {}) {
  // Optional escape-hatches (added Feb 2026 for the hidden /2aestimator page).
  // When NOT passed, behaviour is 100 % identical to before, the public
  // /sell page never passes these so it always hits /api/estimate/calculate.
  //   • calculateApiOverride  → swap the price-calculation endpoint URL
  //   • extraPayload          → extra fields merged into the request body
  //     (used to send the staff PIN to the internal endpoint).
  //   • internalMode          → strips customer-facing CTAs/marketing badges
  //     (PriceLock, Countdown, BeatAnyQuote, Accept Offer, Try Another,
  //     Email-this-offer, TrustStrip, ExitIntentModal, honest-pricing
  //     disclosure). Staff just need the price + listings-analyzed count.
  // ── Restore prior offer from sessionStorage (e.g. user clicked store-credit
  // upsell, went to /refurbished, then hit Back). The snapshot expires after
  // 30 minutes so a stale offer doesn't haunt the page next day. ──
  const SNAPSHOT_KEY = 'ie_offer_snapshot_v1';
  const SNAPSHOT_TTL_MS = 30 * 60 * 1000;
  const initial = (() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(SNAPSHOT_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (!snap || (Date.now() - (snap.ts || 0)) > SNAPSHOT_TTL_MS) {
        sessionStorage.removeItem(SNAPSHOT_KEY);
        return null;
      }
      return snap;
    } catch { return null; }
  })();

  const [query, setQuery] = useState(initial?.query || '');
  const [condition, setCondition] = useState(initial?.condition || 'good');
  const [condTap, setCondTap] = useState(0);
  const conditionRef = useRef(null);
  const specsWereComplete = useRef(false);
  const [result, setResult] = useState(initial?.result || null);
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [expandedCond, setExpandedCond] = useState(null);
  const [category, setCategory] = useState('phone');
  // Initialize with hardcoded fallback chips so users NEVER see skeletons on page load.
  // The API call below still runs to refresh with dynamic device suggestions from the catalog.
  // This guarantees the "Try for example" chips appear instantly even if the API is slow,
  // a service worker is recovering from an old version, or the user is on a cold-start backend.
  // (Pricing logic untouched, these are display-only example chips.)
  const [suggestions, setSuggestions] = useState([
    'iPhone 16 Pro Max 256GB',
    'Samsung Galaxy S24 Ultra 256GB',
    'MacBook Pro 14 M3 Pro',
    'iPad Pro 12.9 M2 256GB',
    'Apple Watch Ultra 2',
    'PlayStation 5 Disc Edition',
    'Meta Quest 3 128GB',
    'Ray-Ban Meta (Gen 2)',
  ]);
  const [infoPulse, setInfoPulse] = useState(true);

  // Sale flow
  const [phase, setPhase] = useState('search'); // search | delivery | contact | success

  // Scroll to top when the success/celebration screen renders so the customer sees it.
  useEffect(() => {
    if (phase === 'success' && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [phase]);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [contact, setContact] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
  const [payMethod, setPayMethod] = useState('');
  const [payHandle, setPayHandle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedQuote, setCopiedQuote] = useState(false);
  const [touched, setTouched] = useState({});

  const searchInputRef = useRef(null);
  const searchCountRef = useRef(0);
  const lastSearchTimeRef = useRef(0);
  const estimateCacheRef = useRef(new Map());

  // Auto-focus the search box on landing so an ad click lands ready-to-type.
  useEffect(() => {
    if (phase === 'search' && !result && !query && searchInputRef.current) {
      try { searchInputRef.current.focus({ preventScroll: true }); } catch { /* older browsers */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markTouched = (field) => setTouched(p => ({ ...p, [field]: true }));

  // Info button pulse timer - stop after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => setInfoPulse(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  // ── Persist the offer so it survives a round-trip to /refurbished (store
  // credit) and back. Cleared explicitly on reset() / Try Another. ──
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (result && !result.error && !result.rate_limited && result.offer_price > 0) {
        sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
          query, condition, result, ts: Date.now(),
        }));
      } else {
        // any non-positive / error state clears the snapshot so we don't restore a broken card
        sessionStorage.removeItem(SNAPSHOT_KEY);
      }
    } catch { /* storage full / disabled, non-blocking */ }
  }, [result, query, condition]);

  useEffect(() => {
    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Expired-quote notice (when the 7-day deep-link is opened too late) ──
  const [expiredOffer, setExpiredOffer] = useState(null);

  // ── Restore quote from email/SMS deep-link (?q=TOKEN) ──
  // When a customer taps the link we sent them, fetch their saved quote and
  // pre-populate the offer card so they land on EXACTLY what we promised.
  // If 7 days have elapsed, show a clear "offer expired, re-evaluate" notice.
  const restoreRanRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (restoreRanRef.current) return;  // StrictMode-safe, only run once per real mount
    const params = new URLSearchParams(window.location.search);
    const token = params.get('q');
    if (!token || token.length < 4 || token.length > 32) return;
    restoreRanRef.current = true;

    // Use XMLHttpRequest (instead of fetch) to bypass third-party fetch wrappers
    // (Microsoft Clarity / GTM) that monkey-patch window.fetch and pre-consume
    // the response body, that was causing r.bodyUsed=true before our handler ran.
    (() => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/api/conversion/quote/${encodeURIComponent(token)}`);
      xhr.responseType = 'json';
      xhr.onload = () => {
        const status = xhr.status;
        const body = xhr.response;  // already parsed JSON
        if (!body) return;
        if (status === 410) {
          const info = body?.detail || body || {};
          setExpiredOffer({
            device_query: info.device_query || '',
            offer_price: info.offer_price || 0,
          });
          return;
        }
        if (status < 200 || status >= 300 || !body.ok) return;
        setQuery(body.device_query || '');
        setCondition(body.condition || 'good');
        setResult({
          offer_price: Number(body.offer_price) || 0,
          items_analyzed: 50,  // display-only placeholder
          recovered_from_sms: true,
        });
      };
      xhr.onerror = () => { /* network error, silently ignore */ };
      xhr.send();
    })();
  }, []);

  // ── Prefill from a "Sell this device" deep-link (?device=NAME) ──
  // Lets any "Sell your device" CTA across the site land the customer directly on
  // the Instant Estimate with their device already typed in, ready to get a price.
  const devicePrefillRanRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || devicePrefillRanRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const dev = params.get('device') || params.get('d');
    if (!dev) return;
    devicePrefillRanRef.current = true;
    const clean = dev.slice(0, 80);
    setQuery(clean);
    setPhase('search');
    setTimeout(() => {
      try {
        const el = document.getElementById('instant-estimate-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (searchInputRef.current) searchInputRef.current.focus({ preventScroll: true });
      } catch { /* non-blocking */ }
    }, 250);
  }, []);


  // Retry-on-failure helper for the suggestion endpoint.
  // Same return shape (calls setSuggestions). Pure additive: no logic/UX change.
  const loadSuggestions = useCallback((attempt = 0) => {
    const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), 8000) : null;
    fetch(`${API_URL}/api/estimate/search-suggestions?_=${Date.now()}`, {
      cache: 'no-store',
      signal: ctl ? ctl.signal : undefined,
    })
      .then(r => { if (!r.ok) throw new Error('http_' + r.status); return r.json(); })
      .then(d => {
        if (timer) clearTimeout(timer);
        if (d && Array.isArray(d.suggestions) && d.suggestions.length > 0) {
          setSuggestions(d.suggestions);
        } else if (attempt < 2) {
          setTimeout(() => loadSuggestions(attempt + 1), 800 * (attempt + 1));
        }
      })
      .catch(() => {
        if (timer) clearTimeout(timer);
        if (attempt < 2) setTimeout(() => loadSuggestions(attempt + 1), 1000 * Math.pow(2, attempt));
      });
  }, []);

  const detectCategory = useCallback((q) => {
    const ql = q.toLowerCase();
    // Buyback-eligible families that don't fit phone/laptop/tablet/watch/console:
    if (/airpods|homepod|apple\s?tv|imac|mac\s?mini|mac\s?studio|mac\s?pro\b/i.test(ql)) return 'accessory';
    if (/meta\s?quest|quest\s?\d|oculus|ray.?ban\s?meta|oakley\s?meta|meta\s?glasses|ray.?ban\s?stories/i.test(ql)) return 'meta';
    if (/orion\s?glasses|vision\s?pro/i.test(ql)) return 'glasses';
    if (/iphone|samsung galaxy s|galaxy s|pixel \d|oneplus|motorola|galaxy z|fold|flip/i.test(ql)) return 'phone';
    if (/macbook|laptop|thinkpad|chromebook|surface laptop|dell xps|hp spectre/i.test(ql)) return 'laptop';
    if (/ipad|galaxy tab|surface pro|tablet|fire hd/i.test(ql)) return 'tablet';
    if (/apple watch|galaxy watch|pixel watch|garmin|fitbit/i.test(ql)) return 'watch';
    if (/playstation|ps[4-9]\b|xbox|nintendo|switch|steam deck/i.test(ql)) return 'console';
    return 'phone';
  }, []);

  useEffect(() => { if (query.length > 2) setCategory(detectCategory(query)); }, [query, detectCategory]);

  // Loading animation with faster timing (snappy ~200ms per step)
  useEffect(() => {
    if (!loading) return;
    setLoadStep(0);
    const iv = setInterval(() => setLoadStep(p => (p < LOADING_STEPS.length - 1 ? p + 1 : p)), 110);
    return () => clearInterval(iv);
  }, [loading]);

  const [specWarning, setSpecWarning] = useState(null); // null or { missing: [...] }
  const [specNudge, setSpecNudge] = useState(null); // null or { missing: [labels] } — guided-spec completeness nudge
  const [specsSkipped, setSpecsSkipped] = useState(false);
  const [pickerResume, setPickerResume] = useState(null); // {cat, brand} to re-open the model list on Back
  const { getSuggestions, getModelSpecs, getDeviceTemplate, hasExtendingMatch, isExactModel, catalogLoading, catalogError, retryCatalog } = useDeviceSuggestions(`${API_URL}/api/estimate/models${internalMode ? '' : '?specs_only=true'}`, { correctSpecs: !internalMode });
  const catalogPending = !internalMode && catalogLoading;
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);

  // Recompute smart suggestions as the customer types (client-side, instant).
  useEffect(() => {
    if (query.trim().length >= 2 && !result) setModelSuggestions(getSuggestions(query));
    else setModelSuggestions([]);
  }, [query, getSuggestions, result]);

  // AI-verified, always-real spec chips for ANY recognized device (catalog, non-catalog,
  // or future models), never offers an impossible combination. We only show the picker
  // for a SPECIFIC model: if the typed text is an ambiguous prefix that still has live
  // "did you mean?" suggestions (and isn't an exact catalog hit), we show the dropdown
  // instead. Catalog specs render instantly; the AI list refines them once it resolves.
  const catalogSpecs = (!result && query.trim().length >= 2) ? getModelSpecs(query) : null;
  const ambiguous = !catalogSpecs && (!result && query.trim().length >= 2) && hasExtendingMatch(query);
  // Keep the original fallback behaviour. Only confirmed per-model spec fixes
  // override the existing options; the model catalogue and names stay untouched.
  const { specs: aiSpecs, loading: specsLoading } = useModelSpecs(`${API_URL}/api/estimate/model-specs`, query, { enabled: !result && !ambiguous && !catalogSpecs && !catalogPending });

  // Universal fallback: when the local catalog has NO match (e.g. free-form, typo, or a
  // YEAR-based search like "macbook pro 2020"), ask the AI for real candidate model names
  // so the customer ALWAYS gets clickable model suggestions. After they pick one, the
  // spec picker resolves its storage/chip/RAM as usual.
  const clientEmpty = modelSuggestions.length === 0;
  const { candidates: aiCandidates, loading: candidatesLoading } = useAiModelCandidates(
    `${API_URL}/api/estimate/model-candidates`,
    query,
    { enabled: !result && query.trim().length >= 3 && clientEmpty && !catalogSpecs && !ambiguous && !catalogPending }
  );
  // When the AI offers MULTIPLE distinct models and the customer hasn't narrowed to one
  // yet, show the model chooser first (prefer it over the single-guess spec picker).
  const _qc = (parseQualifiers(query).model || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const _candCompacts = aiCandidates.map(c => c.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const multiCandidate = aiCandidates.length > 1 && !_candCompacts.some(c => c === _qc);
  // The AI returns the COMPLETE, live spec set for ANY model (current or future), incl.
  // Screen Size + Processor for laptops. Always prefer it once resolved so no spec is ever
  // missing; the instant client template is only a placeholder until the AI responds.
  // IMPORTANT: the template now renders even for an ambiguous/prefix model (e.g. "Google
  // Pixel", "iPhone 15") the moment a device FAMILY is recognized, so the red/green spec
  // pills (Storage, Carrier, RAM, Screen, etc.) ALWAYS guide the customer on what's still
  // needed, for every device type. Exact-model refinement is offered as chips inside the
  // picker (see modelOptions) instead of a separate dropdown.
  const templateSpecs = (!result && !catalogPending && (internalMode || !specsLoading) && query.trim().length >= 3) ? getDeviceTemplate(query) : null;
  // Catalog first (curated, authoritative & STABLE), then AI (for non-catalog models),
  // then the instant template. Prioritising the catalog stops the storage/carrier
  // options from changing under the customer while they pick a spec.
  const modelSpecs = catalogSpecs || aiSpecs || templateSpecs;
  const specsComplete = !!modelSpecs && (modelSpecs.groups || []).length > 0 && (modelSpecs.groups || []).every(g => (modelSpecs.selected || {})[g.key]);
  const dropdownItems = modelSuggestions.length ? modelSuggestions : aiCandidates;
  // Condition step appears only once the device + specs are settled (or skipped).
  const showCondition = query.trim().length >= 3 && (specsComplete || specsSkipped ||
    (!modelSpecs && !catalogPending && !specsLoading && !candidatesLoading && !(suggestOpen && dropdownItems.length > 0)));
  // Exact-model shortcut chips inside the picker. When we're showing the instant template
  // for a recognized family (no exact catalog/AI model locked yet), surface the live model
  // suggestions so the customer can pin the precise model in one tap, while the spec pills
  // already tell them which specs are missing. Otherwise fall back to the multi-candidate set.
  const usingTemplate = !aiSpecs && !catalogSpecs && !!templateSpecs;
  const modelOptions = (catalogSpecs || (!internalMode && isExactModel(query)))
    ? []
    : (usingTemplate && dropdownItems.length
        ? dropdownItems.slice(0, 6)
        : (multiCandidate ? aiCandidates : []));

  const applySpecs = (groupKey, value) => {
    if (!modelSpecs) return;
    // Rebuild from the recognized model + the full set of selected specs (chip/RAM/
    // storage/carrier), the model name is never silently changed.
    const selected = { ...(modelSpecs.selected || {}), [groupKey]: value };
    setQuery(modelSpecs.specsCorrected ? buildSellSpecQuery(modelSpecs, selected, groupKey) : buildSpecQuery(modelSpecs.model, modelSpecs.groups, selected, ''));
    setSpecWarning(null);
    setSpecNudge(null);
    setSpecsSkipped(false);
  };

  const pickSuggestion = (s, autoRun = false) => {
    setQuery(s);
    setSuggestOpen(false);
    setSpecWarning(null);
    setSpecNudge(null);
    setSpecsSkipped(false);
    if (autoRun) setTimeout(() => handleGetEstimate(true, s), 0);
  };

  const specReqs = SPEC_REQUIREMENTS[category] || SPEC_REQUIREMENTS.phone;
  const specStatus = specReqs.map(s => ({ ...s, met: s.regex.test(query) }));

  // Carrier-locked iPhone 17 family (17 / 17 Air / 17 Plus / 17 Pro / 17 Pro Max)
  // → no instant price; route to the manual lead form. Unlocked iPhone 17s are unaffected.
  const isIphone17CarrierLocked = (text) => {
    const t = (text || '').toLowerCase();
    if (!/\biphone\s*17\b/.test(t)) return false;
    const chip = (modelSpecs?.selected?.carrier || '').toLowerCase();
    if (chip) return chip !== 'unlocked' && chip !== 'wifi only';
    if (/\bunlocked\b/.test(t)) return false;
    return /(at&t|at\s*&\s*t|\batt\b|verizon|t-?\s*mobile|tmobile|sprint|cricket|metro|boost|xfinity|us\s*cellular)\b/.test(t);
  };

  const handleGetEstimate = async (forceSkip = false, queryOverride = null) => {
    const q = (queryOverride != null ? queryOverride : query).trim();
    if (!q || condition === 'broken') return;
    setSuggestOpen(false);

    // ── iPhone 17 + CARRIER LOCKED → straight to the manual quote form (no price) ──
    if (isIphone17CarrierLocked(q)) {
      setSpecWarning(null);
      setLoading(false);
      setResult({
        query: q, category: 'phone', condition,
        offer_price: 0, items_analyzed: 0, confidence: 0,
        low_data: true, not_in_catalog: true, carrier_locked_manual: true,
        low_data_message: "Carrier-locked iPhone 17 models are quoted individually by our team. Fill out your info below and we'll send your personalized offer ASAP by email or text.",
      });
      import('../services/analytics').then(m => m.trackInstantEstimateRequested('sell', q)).catch(() => {});
      return;
    }

    // ── Instant cache: identical query + condition returns immediately (no round-trip) ──
    const cacheKey = `${q.toLowerCase()}|${condition}`;
    const cachedResult = estimateCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setSpecWarning(null);
      setSpecNudge(null);
      setResult(cachedResult);
      return;
    }

    // ── Guided-spec completeness nudge: if the spec picker is showing and the
    // customer hasn't chosen every spec (storage / carrier / connectivity / size /
    // edition …), suggest completing it for the most accurate offer — but always
    // let them skip and get the estimate anyway. ──
    if (modelSpecs && !forceSkip && !specsSkipped) {
      const groups = modelSpecs.groups || [];
      const selected = modelSpecs.selected || {};
      const unfilled = groups.filter(g => !selected[g.key]);
      if (unfilled.length > 0) {
        setSpecWarning(null);
        setSpecNudge({ missing: unfilled.map(g => g.label) });
        return;
      }
    }
    setSpecNudge(null);

    // Check for missing specs (skip the "Model" field which should always be detected).
    // Recognized catalog models are guided by the tap-to-build spec chips instead, so
    // they never hit this wall.
    const missingSpecs = specStatus.filter(s => !s.met && s.label !== 'Model');
    const modelKnown = !!getModelSpecs(query) || !!aiSpecs;
    if (missingSpecs.length > 0 && !forceSkip && !specsSkipped && !modelKnown) {
      setSpecWarning({ missing: missingSpecs });
      return;
    }

    setSpecWarning(null);
    // Client-side search throttle removed (owner requirement: no wait interruptions on the front page).
    searchCountRef.current += 1;
    lastSearchTimeRef.current = Date.now();
    // GA4 / Google Ads / Meta Pixel: the quote request itself is a micro-conversion
    import('../services/analytics').then(m => m.trackInstantEstimateRequested('sell', q)).catch(() => {});
    setLoading(true);
    setResult(null);
    setExpandedCond(null);
    const startTime = Date.now();
    try {
      // Optional override + extra-payload escape hatch (used by /2aestimator
      // to call the unrestricted internal endpoint with a PIN). When neither
      // prop is provided, the call is byte-identical to the original.
      const calculateUrl = calculateApiOverride || `${API_URL}/api/estimate/calculate`;
      const payload = { query: q, condition, issues: [], ...(extraPayload || {}) };
      // Resilient fetch: 35s timeout (AbortController) + one automatic retry so a
      // transient slow response or gateway blip doesn't dead-end the customer.
      const fetchEstimate = async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        try {
          const r = await fetch(calculateUrl, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload), signal: controller.signal,
          });
          if (!r.ok) throw new Error(`http_${r.status}`);
          return await r.json();
        } finally { clearTimeout(timer); }
      };
      let data;
      try {
        data = await fetchEstimate();
      } catch {
        await new Promise(r => setTimeout(r, 1200));
        data = await fetchEstimate();
      }
      // Snappy reveal: cap the loading floor so fast responses display almost instantly.
      const minWait = 600;
      const remaining = minWait - (Date.now() - startTime);
      if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
      setResult(data);
      // Cache successful results for instant repeat lookups this session.
      if (data && !data.error && !data.rate_limited && !data.rejected) {
        estimateCacheRef.current.set(cacheKey, data);
      }
      // ── Track conversion event: quote_shown ──
      if (!data?.rate_limited && !data?.error) {
        fetch(`${API_URL}/api/conversion/track-event`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'quote_shown',
            device_query: q,
            offer_price: data?.offer_price || 0,
            condition,
          }),
        }).catch(() => {});
      }
    } catch {
      // Never dead-end the customer. If the estimate can't be fetched (slow
      // backend, gateway timeout, network blip), gracefully route them to the
      // manual-estimate path so they can still place their order and our team
      // follows up with a custom quote, no error screen is ever shown.
      setResult({
        query: q,
        category: 'unknown',
        condition,
        offer_price: 0,
        items_analyzed: 0,
        confidence: 0,
        low_data: true,
        low_data_message: "We're finalizing pricing for this item. Please continue with your order and fill out your info, our team will send you a personalized estimate ASAP by email or text.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance: tapping a condition runs the estimate once every spec is chosen.
  useEffect(() => {
    if (!condTap || !query.trim() || condition === 'broken') return;  // incomplete specs → handleGetEstimate shows the spec nudge
    handleGetEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condTap]);

  // Auto-advance: once the last spec is picked, glide to the condition step.
  useEffect(() => {
    if (specsComplete && !specsWereComplete.current && !result) {
      conditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    specsWereComplete.current = specsComplete;
  }, [specsComplete, result]);

  const reset = () => {
    setQuery(''); setCondition('good'); setResult(null); setExpandedCond(null);
    setPhase('search'); setDeliveryMethod(''); setContact({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
    setPayMethod(''); setPayHandle(''); setAgreed(false); setSmsOptIn(false); setInfoPulse(true); setSubmitting(false);
    setSpecWarning(null); setSpecsSkipped(false); setPickerResume(null); setSpecNudge(null);
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch { /* non-blocking */ }
    setTimeout(() => setInfoPulse(false), 8000);
    // Refetch suggestions for new random examples (keep current chips if API returns empty/fails)
    fetch(`${API_URL}/api/estimate/search-suggestions`)
      .then(r => r.json())
      .then(d => { if (d && Array.isArray(d.suggestions) && d.suggestions.length > 0) setSuggestions(d.suggestions); })
      .catch(() => {});
  };

  const handleSubmitOrder = async () => {
    setSubmitting(true);
    const body = {
      category, brand: 'Instant Estimate', model: query.trim(), storage: 'N/A', condition,
      answers: { source: 'instant_estimate', is_low_data: result?.low_data || false },
      delivery_method: deliveryMethod,
      estimated_price: result?.offer_price || 0, quoted_price: result?.offer_price || 0, estimated_value: result?.offer_price || 0,
      contact_name: contact.name, contact_email: contact.email, contact_phone: contact.phone,
      sms_opt_in: !!smsOptIn,
      sms_opt_in_text: smsOptIn ? SMS_OPT_IN_CONSENT_TEXT : null,
    };
    if (deliveryMethod === 'mail-in') {
      Object.assign(body, { contact_address: contact.address, contact_city: contact.city, contact_state: contact.state, contact_zip: contact.zip, payment_method: payMethod, payment_handle: payHandle });
    }
    try {
      const resp = await fetch(`${API_URL}/api/sell-quote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (resp.ok) {
        setPhase('success');
        // ── Track conversion event: quote_submitted (final conversion) ──
        fetch(`${API_URL}/api/conversion/track-event`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'quote_submitted',
            device_query: query.trim(),
            offer_price: result?.offer_price || 0,
            condition,
            extra: { delivery_method: deliveryMethod },
          }),
        }).catch(() => {});
        // ── Fire ad-network conversion pixels ──
        if (typeof window !== 'undefined') {
          if (window.gtag) {
            window.gtag('event', 'conversion', { event_category: 'sell_quote', value: result?.offer_price || 0, currency: 'USD' });
          }
          if (window.fbq) {
            window.fbq('track', 'Lead', { value: result?.offer_price || 0, currency: 'USD', content_name: query.trim() });
          }
        }
      } else throw new Error();
    } catch { alert('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const canSubmit = () => {
    const hasBase = contact.name && isEmailValid(contact.email) && isPhoneValid(contact.phone) && agreed;
    if (deliveryMethod === 'mail-in') return hasBase && contact.address && contact.city && contact.state && isZipValid(contact.zip) && payMethod && payHandle;
    return hasBase;
  };

  // First invalid required field's testid → so we can scroll/focus it on submit.
  const firstInvalidField = () => {
    if (!contact.name) return 'contact-name';
    if (!isPhoneValid(contact.phone)) return 'contact-phone';
    if (!isEmailValid(contact.email)) return 'contact-email';
    if (deliveryMethod === 'mail-in') {
      if (!contact.address) return 'contact-address';
      if (!contact.city) return 'contact-city';
      if (!contact.state) return 'contact-state';
      if (!isZipValid(contact.zip)) return 'contact-zip';
      if (!payMethod) return `payment-${PAYMENT_METHODS[0]?.id}`;
      if (!payHandle) return 'payment-handle';
    }
    if (!agreed) return 'agree-terms';
    return null;
  };

  // Always-clickable submit: if something's missing, guide the customer to it
  // (focus + scroll) instead of leaving a silently-disabled button.
  const attemptSubmit = () => {
    if (submitting) return;
    if (canSubmit()) { handleSubmitOrder(); return; }
    setTouched({ name: true, phone: true, email: true, address: true, city: true, state: true, zip: true });
    const id = firstInvalidField();
    if (id && typeof document !== 'undefined') {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); try { el.focus({ preventScroll: true }); } catch { /* noop */ } }
    }
  };

  // ── Guidance layer (appearance only — reads existing state, no logic change) ──
  const guideStep = (result && result.offer_price > 0) ? 3 : (query.trim() ? 2 : 1);
  const coachMessage = (() => {
    if (result && result.offer_price > 0) return "🎉 Here's your cash offer! Tap the green button to continue.";
    if (result && (result.low_data || result.carrier_locked_manual)) return "Almost done! Fill in your info below and our team sends your custom price ASAP.";
    if (condition === 'broken') return "Broken devices need an in-person look — tap Contact Us and we'll help.";
    if (query.trim()) return "Nice pick! ✅ Now choose the condition, then tap the big green Get Estimate button 👇";
    return "👋 Hi! Step 1: just tap the device you want to sell below. No typing needed!";
  })();
  // Bring the offer (price + Accept button) into view as soon as a result arrives.
  useEffect(() => {
    if (!result || typeof window === 'undefined') return;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="estimate-result-section"], [data-testid="estimate-low-data-section"]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.top >= 110 && r.top < window.innerHeight * 0.45) return;
      window.scrollTo({ top: Math.max(0, r.top + window.pageYOffset - (window.innerWidth >= 1024 ? 120 : 84)), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [result]);

  const scrollCardTop = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = document.querySelector('[data-testid="instant-estimate"]');
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }, []);
  // Mobile: when calculation starts (robot appears) or the estimate is ready,
  // pull the widget into view so the customer never has to scroll to find it.
  useEffect(() => {
    if (loading || result) scrollCardTop();
  }, [loading, result, scrollCardTop]);
  const handlePickModel = (m, ctx) => { setQuery(m); setResult(null); setSpecWarning(null); setSpecNudge(null); setSpecsSkipped(false); if (ctx && ctx.brand) setPickerResume(ctx); scrollCardTop(); };
  // Always-available Back: from the offer → back to specs; from specs/condition →
  // back to the model list they came from (or the picker). Never dead-ends the user.
  const goBack = () => {
    if (result) { setResult(null); scrollCardTop(); return; }
    setQuery(''); setResult(null); setSpecWarning(null); setSpecNudge(null); setSpecsSkipped(false); scrollCardTop();
  };
  const handlePickOther = () => {
    setQuery(''); setResult(null); setSpecWarning(null); setSpecNudge(null); setSpecsSkipped(false);
    scrollCardTop();
    try { searchInputRef.current?.focus({ preventScroll: true }); } catch { /* older browsers */ }
  };

  // One assistance control in every public phase; internal estimator stays unchanged.
  const contactSupport = !internalMode && <SellContactSupport key="estimate-contact-help" idPrefix="sell-estimate" />;

  // ─────────────────── LOADING OVERLAY ───────────────────
  if (loading) {
    return (
      <div className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto px-2 sm:px-0" data-testid="instant-estimate">
        <div className="ie-card p-4 sm:p-10 text-center">
          <div className="relative inline-block mb-4 sm:mb-6">
            <div className="hidden sm:block"><RobotFace scanning size={90} /></div>
            <div className="block sm:hidden"><RobotFace scanning size={64} /></div>
            <div className="absolute inset-[-12px] sm:inset-[-16px] border-2 border-cyan-500/20 rounded-full animate-spin" style={{ animationDuration: '4s' }} />
            <div className="absolute inset-[-12px] sm:inset-[-16px] animate-spin" style={{ animationDuration: '3s' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/60" />
            </div>
          </div>
          <div className="space-y-1.5 sm:space-y-2 max-w-xs mx-auto">
            {LOADING_STEPS.map((step, i) => (
              <div key={step} className={`text-xs sm:text-sm transition-all duration-500 ${i <= loadStep ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
                {i < loadStep
                  ? <span className="text-cyan-400 flex items-center justify-center gap-1.5"><CheckCircle2 className="w-3 sm:w-3.5 h-3 sm:h-3.5" /> {step}</span>
                  : i === loadStep
                    ? <span className="text-slate-800 flex items-center justify-center gap-1.5"><span className="w-3 h-3 sm:w-3.5 sm:h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin inline-block" /> {step}</span>
                    : <span className="text-slate-600">{step}</span>
                }
              </div>
            ))}
          </div>
        </div>
        {contactSupport}
        <IEStyles />
      </div>
    );
  }

  // ─────────────────── SUCCESS (with Congratulations effect) ───────────────────
  if (phase === 'success') {
    return (
      <div className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto px-2 sm:px-0" data-testid="instant-estimate-success">
        <div className="ie-card p-4 sm:p-8 text-center relative overflow-hidden">
          <Confetti />
          <div className="relative z-10">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-3 sm:mb-4 ie-success-bounce">
              <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" />
            </div>
            <h2 className="text-xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 mb-1.5 sm:mb-2 ie-success-text">
              Congratulations!
            </h2>
            <p className="text-lg font-bold text-slate-900 mb-1">Order Submitted Successfully</p>
            {result?.low_data ? (
              <p className="text-slate-400 text-sm mb-5 max-w-sm mx-auto">An agent will contact you at <strong className="text-slate-800">{contact.email}</strong> with a custom estimate.</p>
            ) : (
              <p className="text-slate-400 text-sm mb-5 max-w-sm mx-auto">{deliveryMethod === 'mail-in' ? `We'll send a free prepaid shipping label to ${contact.email}.` : 'Visit our store during business hours. Your order is ready.'}</p>
            )}
            {deliveryMethod === 'walk-in' && (
              <div className="ie-inner-card p-3 mb-5 text-left text-xs">
                <p className="text-slate-700 font-semibold mb-1">Store Location</p>
                <p className="text-slate-400">4859 W Irving Park Rd, Chicago, IL 60641</p>
                <p className="text-slate-400">Mon-Fri 10AM-6PM, Sat 10AM-4PM</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={reset} className="ie-btn-primary px-6 py-2.5 text-sm" data-testid="new-estimate-success-btn">Get Another Estimate</button>
              <a href="/sell-status" className="ie-btn-ghost px-6 py-2.5 text-sm">Track Order</a>
            </div>
            <OrderFaq type="sell" />
          </div>
        </div>
        {contactSupport}
        <IEStyles />
      </div>
    );
  }

  // ─────────────────── CONTACT PHASE ───────────────────
  if (phase === 'contact') {
    return (
      <div className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto px-2 sm:px-0" data-testid="instant-estimate-contact">
        <div className="ie-card">
          <IEHeader result={result} query={query} onReset={reset} />
          <StepProgress step={2} accent="emerald" />
          <div className="px-3 sm:px-5 pb-2">
            <h3 className="text-lg font-bold text-slate-900 text-center mb-1">Your Information</h3>
            <p className="text-slate-400 text-xs text-center mb-4">{deliveryMethod === 'mail-in' ? "We'll send a free shipping label to your address" : 'For order confirmation and updates'}</p>
          </div>
          <div className="px-3 sm:px-5 pb-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-400 text-xs mb-1">Full Name *</label>
                <Input value={contact.name} onChange={e => setContact(p => ({...p, name: e.target.value}))} onBlur={() => markTouched('name')} placeholder="John Doe" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-name" {...FIELD_PROPS.name} />
                <FieldStatus valid={!!contact.name} touched={touched.name} hint="Enter your name" />
              </div>
              <div>
                <label className="block text-slate-400 text-xs mb-1">Phone *</label>
                <Input value={contact.phone} onChange={e => setContact(p => ({...p, phone: formatPhoneInput(e.target.value)}))} onBlur={() => markTouched('phone')} placeholder="(555) 123-4567" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-phone" {...FIELD_PROPS.phone} />
                <FieldStatus valid={isPhoneValid(contact.phone)} touched={touched.phone} hint="Enter a 10-digit phone number" />
              </div>
            </div>
            <div>
              <label className="block text-slate-400 text-xs mb-1">Email *</label>
              <Input value={contact.email} onChange={e => setContact(p => ({...p, email: e.target.value}))} onBlur={() => markTouched('email')} placeholder="you@email.com" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-email" {...FIELD_PROPS.email} />
              <FieldStatus valid={isEmailValid(contact.email)} touched={touched.email} hint="Enter a valid email address" />
            </div>
            {deliveryMethod === 'mail-in' && (
              <>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-2">Shipping Address</p>
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Street Address *</label>
                  <Input value={contact.address} onChange={e => setContact(p => ({...p, address: e.target.value}))} placeholder="123 Main St" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-address" {...FIELD_PROPS.address} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div><label className="block text-slate-400 text-xs mb-1">City *</label><Input value={contact.city} onChange={e => setContact(p => ({...p, city: e.target.value}))} placeholder="Chicago" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-city" {...FIELD_PROPS.city} /></div>
                  <div><label className="block text-slate-400 text-xs mb-1">State *</label>
                    <select value={contact.state} onChange={e => setContact(p => ({...p, state: e.target.value}))} className="w-full h-9 rounded-md bg-slate-50 border border-slate-200 text-slate-900 text-sm px-2 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 outline-none" data-testid="contact-state" {...FIELD_PROPS.state}>
                      <option value="">Select</option>
                      {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-1"><label className="block text-slate-400 text-xs mb-1">ZIP *</label><Input value={contact.zip} onChange={e => setContact(p => ({...p, zip: e.target.value.replace(/\D/g, '').slice(0,5)}))} placeholder="60641" className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="contact-zip" {...FIELD_PROPS.zip} /></div>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-2">Payment Method</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(pm => (
                    <button key={pm.id} onClick={() => setPayMethod(pm.id)} className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${payMethod === pm.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'}`} data-testid={`payment-${pm.id}`}>{pm.name}</button>
                  ))}
                </div>
                {payMethod && (
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">{PAYMENT_METHODS.find(p => p.id === payMethod)?.name} Handle *</label>
                    <Input value={payHandle} onChange={e => setPayHandle(e.target.value)} placeholder={PAYMENT_METHODS.find(p => p.id === payMethod)?.placeholder} className="bg-slate-50 border-slate-200 text-slate-900 text-sm" data-testid="payment-handle" />
                  </div>
                )}
              </>
            )}
            <div className="flex items-start gap-2 pt-2">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500/20" data-testid="agree-terms" />
              <p className="text-[10px] text-slate-500 leading-relaxed">
                I agree that the quoted price is an estimate and may vary after inspection. I confirm this device is legally mine, not stolen, and I have backed up and removed all personal data. I have read and agree to the <a href="/terms-and-conditions" className="text-emerald-400 hover:underline">Terms of Service</a> and <a href="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</a>.
              </p>
            </div>
            <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} accentColor="emerald" testIdPrefix="sell-sms-opt-in" />
            <ReassuranceStrip variant="sell" accent="emerald" className="pt-1" />
            <div className="flex gap-2 pt-1">
              <button onClick={() => setPhase('delivery')} className="ie-btn-ghost px-4 py-2.5 text-sm" data-testid="contact-back-btn">Back</button>
              <button onClick={attemptSubmit} disabled={submitting} className={`flex-1 ie-btn-submit py-2.5 text-sm ${submitting ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="submit-order-btn">
                {submitting ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> Submitting...</> : <><Send className="w-4 h-4 inline mr-1.5" /> {deliveryMethod === 'mail-in' ? 'Submit & Get Shipping Label' : 'Submit Order'}</>}
              </button>
            </div>
          </div>
        </div>
        {contactSupport}
        <IEStyles />
      </div>
    );
  }

  // ─────────────────── DELIVERY PHASE ───────────────────
  if (phase === 'delivery') {
    return (
      <div className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto px-2 sm:px-0" data-testid="instant-estimate-delivery">
        <div className="ie-card">
          <IEHeader result={result} query={query} onReset={reset} />
          <StepProgress step={1} accent="emerald" />
          <div className="px-3 sm:px-5 pb-3">
            <div className="text-center p-4 rounded-xl bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-500/10">
              <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">Your Offer</p>
              {result?.low_data
                ? <p className="text-xl font-bold text-amber-400">Custom Estimate Pending</p>
                : <p className="text-3xl font-black text-slate-900" data-testid="delivery-price">${Math.round(result?.offer_price)}</p>
              }
              <p className="text-slate-500 text-[10px] mt-1 truncate">{query} &middot; <span className="capitalize">{result?.condition || condition}</span></p>
            </div>
            <ReassuranceStrip variant="sell" accent="emerald" className="mt-3" />
          </div>
          {result?.low_data && (
            <div className="px-3 sm:px-5 pb-3"><div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 flex items-start gap-2"><Mail className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /><p className="text-xs text-amber-300 leading-relaxed">An agent will contact you by email with a custom estimate.</p></div></div>
          )}
          <div className="px-3 sm:px-5 pb-2">
            <h3 className="text-lg font-bold text-slate-900 text-center mb-1">How to Proceed?</h3>
            <p className="text-slate-400 text-xs text-center mb-4">Choose how you want to send your device</p>
          </div>
          <div className="px-3 sm:px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div onClick={() => setDeliveryMethod('mail-in')} className={`relative p-4 sm:p-5 rounded-2xl border-2 cursor-pointer transition-all ${deliveryMethod === 'mail-in' ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid="delivery-mail-in">
              <div className="absolute -top-3 -right-3 px-3 py-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full">FREE SHIPPING</div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-3"><Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
              <h4 className="text-base sm:text-lg font-bold text-slate-900 mb-1">Mail-In</h4>
              <p className="text-slate-400 text-xs mb-3">Ship from anywhere in the US</p>
              <ul className="space-y-1.5 text-xs text-slate-600">
                <li className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-purple-400" /> Free prepaid label</li>
                <li className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-yellow-400" /> Fast payment</li>
              </ul>
              <div className="mt-3 p-2 rounded-lg bg-amber-500/5 border border-amber-500/15">
                <p className="text-[10px] text-amber-400/90 leading-relaxed">Package is insured up to $100 by UPS. We are not responsible for damage or lost delivery.</p>
              </div>
              {deliveryMethod === 'mail-in' && <div className="absolute top-3 left-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
            </div>
            <div onClick={() => setDeliveryMethod('walk-in')} className={`relative p-4 sm:p-5 rounded-2xl border-2 cursor-pointer transition-all ${deliveryMethod === 'walk-in' ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid="delivery-walk-in">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-3"><MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
              <h4 className="text-base sm:text-lg font-bold text-slate-900 mb-1">Walk-In</h4>
              <p className="text-slate-400 text-xs mb-3">Visit our Chicago store</p>
              <ul className="space-y-1.5 text-xs text-slate-600">
                <li className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-400" /> Same-day payment</li>
                <li className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> Instant inspection</li>
              </ul>
              {deliveryMethod === 'walk-in' && <div className="absolute top-3 left-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
            </div>
          </div>
          {deliveryMethod === 'walk-in' && (
            <div className="px-3 sm:px-5 pb-3"><div className="p-4 bg-gradient-to-r from-emerald-900/20 to-green-900/20 rounded-xl border border-emerald-500/20">
              <h4 className="text-sm font-semibold text-slate-900 mb-1">Walk-ins Welcome!</h4>
              <p className="text-slate-600 text-xs mb-1">Mon-Fri 10AM-6PM, Sat 10AM-4PM</p>
              <p className="text-slate-400 text-xs">4859 W Irving Park Rd, Chicago, IL 60641</p>
              <a href="https://www.google.com/maps/dir/?api=1&destination=4859+W+Irving+Park+Rd,+Chicago,+IL+60641" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-400 hover:underline"><MapPin className="w-3 h-3" /> Get Directions</a>
            </div></div>
          )}
          <div className="px-3 sm:px-5 pb-5 flex gap-2">
            <button onClick={() => setPhase('search')} className="ie-btn-ghost px-4 py-2.5 text-sm" data-testid="delivery-back-btn">Back</button>
            <button onClick={() => { if (deliveryMethod) setPhase('contact'); }} disabled={!deliveryMethod} className={`flex-1 ie-btn-primary py-2.5 text-sm ${!deliveryMethod ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="delivery-continue-btn">
              Continue <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
        {contactSupport}
        <IEStyles />
      </div>
    );
  }

  // ─────────────────── SEARCH / RESULT PHASE (default) ───────────────────
  return (
    <div className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto px-2 sm:px-0" data-testid="instant-estimate">
      {/* ── Expired-quote banner (shown when ?q=TOKEN points to a 7+ day-old offer) ── */}
      {expiredOffer && (
        <div className="ie-expired-banner" data-testid="offer-expired-banner">
          <div className="ie-expired-icon">⏰</div>
          <div className="ie-expired-body">
            <h3 className="ie-expired-title">Your 21-day offer has expired</h3>
            <p className="ie-expired-text">
              {expiredOffer.device_query
                ? <>The <strong>${Math.round(expiredOffer.offer_price || 0)}</strong> quote for your <strong>{expiredOffer.device_query}</strong> was valid for 21 days and has now expired.</>
                : <>This offer link is more than 21 days old.</>}
              {' '}Market prices change, so we need to re-evaluate, just search your device again below for a fresh, fair price.
            </p>
            <button onClick={() => setExpiredOffer(null)} className="ie-expired-cta" data-testid="offer-expired-dismiss">
              Re-evaluate now →
            </button>
          </div>
        </div>
      )}
      <div className="ie-card relative overflow-hidden">
        {/* Decorative circuit pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #06b6d4 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        {/* ── Robot Header ── */}
        <div className="relative flex items-center gap-2.5 sm:gap-4 px-3 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-3">
          <div className="hidden sm:block"><RobotFace scanning={false} size={56} /></div>
          <div className="block sm:hidden"><RobotFace scanning={false} size={40} /></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm sm:text-xl font-black text-slate-900 leading-tight tracking-tight flex items-center gap-1.5 sm:gap-2" data-testid="estimate-title">
              Instant Estimate
              <span className="ie-ai-badge">AI</span>
              <span className="ie-recommended-badge">Recommended</span>
            </h2>
            <p className="text-[9px] sm:text-[11px] text-slate-500 mt-0.5">Powered by real-time market data</p>
            <span
              data-testid="live-prices-badge"
              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] sm:text-[11px] font-semibold text-emerald-700"
              title="Every estimate is calculated from live eBay listings at the moment you search"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live eBay prices<span className="hidden sm:inline">, checked when you search</span>
            </span>
          </div>
          {result && (
            <button onClick={reset} className="ie-btn-ghost px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs" data-testid="new-estimate-btn">
              <RotateCcw className="w-3 h-3 inline mr-0.5 sm:mr-1" /> New
            </button>
          )}
        </div>

        {/* ── Guided step bar (appearance only) ── */}
        <GuideProgress step={guideStep} />

        {/* ── Always-visible Back button (never dead-ends the customer) ── */}
        {(!!result || query.trim().length > 2) && (
          <div className="px-3 sm:px-5 pt-0.5 pb-1 relative z-10">
            <button
              onClick={goBack}
              data-testid="estimate-back-btn"
              className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-emerald-600 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          </div>
        )}

        {/* ── Search bar ── */}
        {!result && (
          <div className="px-3 sm:px-5 pb-2 relative z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                ref={searchInputRef}
                type="text" value={query}
                onChange={e => { setQuery(e.target.value); setResult(null); setSpecWarning(null); setSpecNudge(null); setSpecsSkipped(false); setSuggestOpen(true); }}
                onFocus={() => setSuggestOpen(true)}
                onKeyDown={e => { if (e.key === 'Enter') { setSuggestOpen(false); handleGetEstimate(); } if (e.key === 'Escape') setSuggestOpen(false); }}
                placeholder="iPhone 16 Pro Max 256GB Unlocked..."
                className="ie-input w-full pl-9 sm:pl-10 pr-9 py-2.5 sm:py-3 text-[13px] sm:text-sm"
                data-testid="estimate-search-input"
                autoComplete="off"
              />
              {query && (
                <button onClick={() => { setQuery(''); setResult(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-5 sm:h-5 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center transition-colors" data-testid="clear-search-btn">
                  <X className="w-3.5 h-3.5 sm:w-3 sm:h-3 text-slate-400" />
                </button>
              )}
            </div>

            {/* Guided spec chips once a model is recognized; otherwise live model
                suggestions (local catalog, or AI candidates for free-form/year queries). */}
            {modelSpecs ? (
              <SpecPicker
                specs={modelSpecs}
                onChange={applySpecs}
                onClear={() => { setQuery(''); setResult(null); }}
                testIdPrefix="sell-spec"
                modelOptions={modelOptions}
                onPickModel={(m) => pickSuggestion(m)}
              />
            ) : catalogPending && catalogError && query.trim().length >= 3 ? (
              <div role="status" className="mt-2.5 flex flex-wrap items-center gap-2 px-3.5 py-3 text-xs text-slate-600" data-testid="sell-catalog-error">
                <span data-testid="sell-catalog-error-message">Device details are temporarily unavailable.</span>
                <button type="button" onClick={retryCatalog} className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors" data-testid="sell-catalog-retry">
                  <RotateCcw className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            ) : ((catalogPending && query.trim().length >= 3) || specsLoading || candidatesLoading) && dropdownItems.length === 0 ? (
              <SpecLoading accent="emerald" />
            ) : (suggestOpen && !result && dropdownItems.length > 0 && (
              <SuggestionDropdown
                suggestions={dropdownItems}
                onPick={(s) => pickSuggestion(s)}
                onSkip={() => setSuggestOpen(false)}
                heading="Did you mean?"
                skipLabel="Skip, search exactly what I typed"
                accent="emerald"
                testIdPrefix="sell-suggestion"
              />
            ))}

            {/* Big tap-first device picker (replaces tiny example chips) */}
            {query.length <= 2 && (
              <DevicePicker resume={pickerResume} onPickModel={handlePickModel} onOther={handlePickOther} onTap={scrollCardTop} />
            )}
          </div>
        )}

        {/* ── Condition selector ── */}
        {!result && showCondition && (
          <div className="px-3 sm:px-5 pb-2 relative z-10 scroll-mt-24 ie-fade-in" ref={conditionRef} data-testid="condition-section">
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-1.5 sm:mb-2">Condition</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2">
              {CONDITIONS.map(c => {
                const active = condition === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => { setCondition(c.id); setResult(null); setCondTap(n => n + 1); }}
                    className={`ie-cond-btn ${active ? 'ie-cond-active' : ''}`}
                    style={active ? { '--cond-color': c.color, borderColor: c.color, backgroundColor: c.color + '15' } : {}}
                    data-testid={`condition-${c.id}`}
                  >
                    <c.Icon className="w-4 sm:w-4 h-4 sm:h-4" style={active ? { color: c.color } : {}} />
                    <span className="text-[11px] sm:text-[11px] font-semibold">{c.label}</span>
                    {/* Info toggle with pulse animation.
                        Rendered as a <span role="button"> instead of <button> to avoid
                        the "<button> cannot be a descendant of <button>" hydration warning. */}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={e => { e.stopPropagation(); setExpandedCond(expandedCond === c.id ? null : c.id); }}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setExpandedCond(expandedCond === c.id ? null : c.id); } }}
                      className={`absolute -top-1.5 -right-1.5 w-6 h-6 sm:w-5 sm:h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors z-10 cursor-pointer ${infoPulse ? 'ie-info-pulse' : ''}`}
                      data-testid={`condition-info-${c.id}`}
                      aria-label={`More about ${c.label} condition`}
                    >
                      {expandedCond === c.id ? <ChevronDown className="w-3 h-3 sm:w-2.5 sm:h-2.5" /> : <Info className="w-3 h-3 sm:w-2.5 sm:h-2.5" />}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Gentle hint to check condition details */}
            {!expandedCond && infoPulse && (
              <p className="text-[10px] text-cyan-400/60 text-center mt-1.5 ie-fade-in">Tap the info icon on each condition to see details</p>
            )}

            {/* ── Condition info expands BELOW the buttons ── */}
            {expandedCond && (() => {
              const c = CONDITIONS.find(x => x.id === expandedCond);
              if (!c) return null;
              return (
                <div className="mt-3 rounded-xl border overflow-hidden transition-all" style={{ borderColor: c.color + '40', backgroundColor: c.color + '08' }} data-testid="condition-info-expanded">
                  <div className="ie-cond-hero relative h-32 sm:h-40" style={{ background: `linear-gradient(135deg, ${c.color}30, ${c.color}10)` }}>
                    <img src={c.image} alt={`${c.label} - sell at 2A Electronics Chicago`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                    {/* Inline-style gradient avoids the global html.light from-slate-900 → solid-white override that was hiding the image. */}
                    <div
                      className="absolute inset-0 pointer-events-none ie-cond-hero-overlay"
                      style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.35) 45%, rgba(15,23,42,0) 85%)' }}
                    />
                    <div className="absolute bottom-3 left-4 flex items-center gap-2 z-10">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.color + '25' }}>
                        <c.Icon className="w-4 h-4" style={{ color: c.color }} />
                      </div>
                      <span className="text-base font-bold drop-shadow-md" style={{ color: '#ffffff' }}>{c.label} Condition</span>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">What to look for</p>
                    <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                      {c.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: c.color + '20' }}>
                            <Check className="w-2.5 h-2.5" style={{ color: c.color }} />
                          </div>
                          <span className="text-slate-600 text-xs leading-relaxed">{h}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="rounded-lg p-3" style={{ backgroundColor: c.color + '10', borderLeft: `3px solid ${c.color}` }}>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        <span className="font-semibold" style={{ color: c.color }}>Tip: </span>{c.tip}
                      </p>
                    </div>
                    <button
                      onClick={() => { setCondition(expandedCond); setExpandedCond(null); setCondTap(n => n + 1); }}
                      className="w-full mt-3 py-2.5 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110"
                      style={{ backgroundColor: c.color }}
                      data-testid="condition-info-select"
                    >
                      Select {c.label}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Spec Warning + Smart Suggestions ── */}
        {!result && specWarning && (
          <div className="px-3 sm:px-5 pb-2 relative z-10" data-testid="spec-warning-section">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Search className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 mb-1">Pick your exact device</p>
                  <p className="text-xs text-slate-400 mb-2.5">
                    Tap your model below for the most accurate offer, or skip and we'll match it for you.
                  </p>
                  {getSuggestions(query).length > 0 ? (
                    <SuggestionDropdown
                      suggestions={getSuggestions(query)}
                      onPick={(s) => pickSuggestion(s, true)}
                      onSkip={() => { setSpecWarning(null); setSpecsSkipped(true); handleGetEstimate(true); }}
                      heading="Tap your model"
                      skipLabel="Skip &amp; get my estimate"
                      accent="emerald"
                      testIdPrefix="sell-spec-suggestion"
                    />
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSpecWarning(null); document.querySelector('[data-testid="estimate-search-input"]')?.focus(); }}
                        className="flex-1 ie-btn-primary py-2 text-xs"
                        data-testid="add-details-btn"
                      >
                        Add More Details
                      </button>
                      <button
                        onClick={() => { setSpecWarning(null); setSpecsSkipped(true); handleGetEstimate(true); }}
                        className="ie-btn-ghost px-3 py-2 text-xs"
                        data-testid="skip-specs-btn"
                      >
                        Skip &amp; Get Estimate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Guided-spec completeness nudge (task: prompt to fill all specs, keep skip) ── */}
        {!result && !specWarning && specNudge && (
          <div className="px-3 sm:px-5 pb-2 relative z-10" data-testid="spec-nudge-section">
            <div className="rounded-xl bg-amber-50 border border-amber-300/70 p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 mb-1">Add a few details for your most accurate offer</p>
                  <p className="text-xs text-slate-500 mb-3">
                    Tap to choose your <strong className="text-amber-600">{specNudge.missing.join(', ')}</strong> above. It only takes a second and locks in an exact price, or skip and we'll confirm it after inspection.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setSpecNudge(null); document.querySelector('[data-testid="sell-spec-picker"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                      className="flex-1 ie-btn-primary py-2 text-xs"
                      data-testid="spec-nudge-fill-btn"
                    >
                      Fill in the details
                    </button>
                    <button
                      onClick={() => { setSpecNudge(null); setSpecsSkipped(true); setTimeout(() => conditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); }}
                      className="ie-btn-ghost px-3 py-2 text-xs"
                      data-testid="spec-nudge-skip-btn"
                    >
                      Skip &amp; Get Estimate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Get Estimate / Broken CTA ── */}
        {!result && !specWarning && !specNudge && (
          <div className="px-3 sm:px-5 py-2.5 sm:py-4 relative z-10">
            {condition === 'broken' ? (
              <div className="text-center py-2">
                <p className="text-xs text-slate-400 mb-3">Broken devices need in-person evaluation</p>
                <a href="/contact" className="ie-btn-ghost px-6 py-2.5 text-sm inline-flex items-center gap-1.5" data-testid="contact-broken-link"><Phone className="w-3.5 h-3.5" /> Contact Us</a>
              </div>
            ) : (
              <>
                {query.trim() && <GuidePointer label="Tap here to see your cash offer" />}
                <button onClick={() => handleGetEstimate()} disabled={!query.trim()} className={`w-full ie-btn-primary py-3 text-sm ${!query.trim() ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="get-estimate-btn">
                  <Bot className="w-4 h-4 inline mr-1.5" /> Get Estimate
                </button>
              </>
            )}
            {!result && <ReassuranceStrip variant="sell" accent="emerald" className="mt-3" />}
          </div>
        )}

        {/* ─── RESULT: Rate Limited ─── */}
        {result && result.rate_limited && (
          <div className="px-3 sm:px-5 pb-4 sm:pb-5 relative z-10">
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-5 text-center">
              <Ban className="w-8 h-8 text-red-400/60 mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900 mb-1">Too Many Requests</p>
              <p className="text-xs text-slate-400 mb-3">Please wait a moment and try again.</p>
              <button onClick={reset} className="ie-btn-ghost px-6 py-2.5 text-sm">Try Again</button>
            </div>
          </div>
        )}

        {/* ─── RESULT: Rejected (TV/Appliance) ─── */}
        {result && result.rejected && (
          <div className="px-3 sm:px-5 pb-4 sm:pb-5 relative z-10" data-testid="estimate-rejected-section">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 text-center">
              <Ban className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900 mb-1">Item Not Accepted</p>
              <p className="text-xs text-slate-400 mb-3 max-w-sm mx-auto">{result.rejected_message}</p>
              <button onClick={reset} className="ie-btn-ghost px-6 py-2.5 text-sm">Search Another Device</button>
            </div>
          </div>
        )}

        {/* ─── RESULT: Normal Offer ─── */}
        {result && !result.error && !result.is_broken && !result.low_data && !result.rejected && !result.rate_limited && result.offer_price > 0 && (
          <div className="px-3 sm:px-5 pb-4 sm:pb-5 relative z-10" data-testid="estimate-result-section">
            <div className="ie-offer-card">
              <div className="text-center py-4 sm:py-6 px-3 sm:px-4 ie-result-cols">
                <div className="ie-result-main">
                <div className="ie-ai-badge mb-3 inline-flex"><Bot className="w-3 h-3 mr-1" /> Estimated Offer</div>
                <p className="text-3xl sm:text-5xl md:text-6xl font-black ie-offer-price tracking-tight leading-none" data-testid="offer-price">
                  ${Math.round(result.offer_price)}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2" data-testid="offer-device-row">
                  <img src={getDeviceImage(result?.device_name || query, result?.category)} alt="" loading="lazy" className="w-8 h-8 rounded-lg object-contain bg-slate-100 flex-shrink-0" />
                  <p className="text-slate-600 text-xs sm:text-sm truncate max-w-[220px]" data-testid="offer-device-model">{query}</p>
                </div>
                <p className="text-slate-500 text-[10px] mt-0.5">Condition: <span className="capitalize text-slate-600">{condition}</span></p>
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-600">Protected Offer</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-[10px] text-slate-500">{result.items_analyzed} listings analyzed</span>
                </div>

                {result.resale_bonus > 0 && (
                  <div className="mt-3 flex justify-center" data-testid="resale-bonus-badge">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-800 text-xs font-bold">
                      <Sparkles className="w-3.5 h-3.5" /> Includes ${result.resale_bonus} high-value bonus
                    </span>
                  </div>
                )}

                {!internalMode && (
                  <div className="mt-4 flex gap-2 max-w-sm mx-auto" data-testid="offer-primary-actions">
                    <button onClick={() => setPhase('delivery')} className="flex-1 ie-btn-primary py-3.5 sm:py-3 text-[15px] sm:text-sm min-h-[52px] sm:min-h-0" data-testid="continue-sale-btn">
                      Accept Offer <ArrowRight className="w-4 h-4 inline ml-1" />
                    </button>
                    <button onClick={reset} className="ie-btn-ghost px-4 py-3.5 sm:py-3 text-[14px] sm:text-sm min-h-[52px] sm:min-h-0" data-testid="try-another-btn">Try Another</button>
                  </div>
                )}
                </div>
                <div className="ie-result-side">
                {/* ── Internal-only: one-tap copy of the quote (for staff to paste in SMS / chat) ── */}
                {internalMode && (
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={async () => {
                        const text = `$${Math.round(result.offer_price)} · ${query} · ${condition.charAt(0).toUpperCase() + condition.slice(1)} condition`;
                        try {
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            await navigator.clipboard.writeText(text);
                          } else {
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }
                          setCopiedQuote(true);
                          setTimeout(() => setCopiedQuote(false), 1800);
                        } catch { /* clipboard blocked, silent */ }
                      }}
                      className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-xs font-medium transition-all ${copiedQuote ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300'}`}
                      data-testid="copy-quote-btn"
                      title="Copy offer to clipboard"
                    >
                      {copiedQuote
                        ? <><Check className="w-3.5 h-3.5" /> Copied to clipboard</>
                        : <><Copy className="w-3.5 h-3.5" /> Copy quote</>
                      }
                    </button>
                  </div>
                )}

                {/* ── Customer-facing marketing block (hidden in internalMode on /2aestimator) ── */}
                {!internalMode && (
                  <>
                    {/* ── Trust badge: price lock ── */}
                    <div className="ie-trust-row" data-testid="offer-trust-badges">
                      <PriceLockBadge />
                    </div>
                    {/* ── Phone/text may pay more ── */}
                    <PhonePriceNote idPrefix="offer" className="mt-3 max-w-sm mx-auto" />
                    <EstimateContactBar idPrefix="offer-contact" heading="Want a better price? Call or text us"
                      deviceQuery={query} offerPrice={result.offer_price} condition={condition} className="mt-3 max-w-sm mx-auto" />
                    {/* Honest pricing disclosure */}
                    <p className="ie-sell-disclosure-standalone" data-testid="offer-disclosure">
                      Honest pricing, final payout matches the actual condition. Non-original parts, battery service, or financed devices may adjust the number; we'll always explain every dollar.
                      <a href="/terms" target="_blank" rel="noreferrer" className="ie-sell-disclosure-link"> View payout terms →</a>
                    </p>
                  </>
                )}
                {/* Specs skipped warning */}
                {specsSkipped && (
                  <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/20 p-2.5 max-w-sm mx-auto" data-testid="specs-skipped-warning">
                    <p className="text-[10px] text-amber-300 leading-relaxed text-center">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      <strong>Heads up:</strong> a few specs (storage, carrier) weren't provided, your final offer adjusts up or down once we confirm them.
                    </p>
                  </div>
                )}
                </div>
              </div>

              {!internalMode && (
                <>
                  {/* ── Email-offer button: friction-removal CTA (works today; SMS pending verification) ── */}
                  <div className="px-3 sm:px-4 pb-3 flex flex-wrap justify-center gap-2">
                    <SmsOfferButton
                      deviceQuery={query}
                      offerPrice={result.offer_price}
                      condition={condition}
                    />
                    <EmailOfferButton
                      deviceQuery={query}
                      offerPrice={result.offer_price}
                      condition={condition}
                    />
                  </div>

                  {/* ── Trust strip ── */}
                  <TrustStrip />

                  {/* ── Exit-intent modal (renders when user moves to leave) ── */}
                  <ExitIntentModal deviceQuery={query} offerPrice={result.offer_price} />
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── RESULT: Low Data ($0) / Not in Catalog ─── */}
        {result && !result.error && !result.rejected && !result.rate_limited && result.low_data && (
          <div className="px-3 sm:px-5 pb-4 sm:pb-5 relative z-10" data-testid="estimate-low-data-section">
            <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 sm:p-5">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3"><AlertCircle className="w-5 h-5 text-amber-400" /></div>
                <p className="text-base sm:text-lg font-bold text-slate-900 mb-1">{result.carrier_locked_manual ? 'Carrier-Locked iPhone 17 — Custom Quote' : (result.not_in_catalog ? 'We Need to Send You a Manual Estimate' : 'Pricing Verification Needed')}</p>
                <p className="text-xs text-slate-400 mb-1">{result.carrier_locked_manual ? 'Carrier-locked iPhone 17 models are priced individually — our team will send your personalized offer ASAP.' : (result.not_in_catalog ? 'Our team will review this item and get you a custom quote ASAP!' : 'We need to verify the pricing on this item.')}</p>
                {phase !== 'low-data-submitted' && <p className="text-xs text-emerald-800 font-medium max-w-sm mx-auto">Please proceed with your order and fill out your info below, we'll contact you shortly with your personalized offer.</p>}
              </div>
              {phase !== 'low-data-submitted' && (
                <EstimateContactBar idPrefix="manual-contact" heading="Prefer to talk? Skip the form"
                  deviceQuery={query} condition={condition} className="mb-4 max-w-sm mx-auto" />
              )}
              {/* Inline contact form */}
              {phase !== 'low-data-submitted' ? (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-500 text-[10px] mb-1">Full Name *</label>
                      <Input value={contact.name} onChange={e => setContact(p => ({...p, name: e.target.value}))} placeholder="John Doe" className="bg-slate-50 border-slate-200 text-slate-900 text-sm h-9" data-testid="low-data-name" {...FIELD_PROPS.name} />
                    </div>
                    <div>
                      <label className="block text-slate-500 text-[10px] mb-1">Phone *</label>
                      <Input value={contact.phone} onChange={e => setContact(p => ({...p, phone: formatPhoneInput(e.target.value)}))} placeholder="(555) 123-4567" className="bg-slate-50 border-slate-200 text-slate-900 text-sm h-9" data-testid="low-data-phone" {...FIELD_PROPS.phone} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-[10px] mb-1">Email *</label>
                    <Input value={contact.email} onChange={e => setContact(p => ({...p, email: e.target.value}))} placeholder="you@email.com" className="bg-slate-50 border-slate-200 text-slate-900 text-sm h-9" data-testid="low-data-email" {...FIELD_PROPS.email} />
                  </div>
                  <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} accentColor="emerald" testIdPrefix="sell-sms-opt-in-lowdata" compact />
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={async () => {
                        if (!contact.name || !isEmailValid(contact.email) || !isPhoneValid(contact.phone)) { alert('Please enter your name, a valid email and a 10-digit phone number.'); return; }
                        setSubmitting(true);
                        try {
                          const r = await fetch(`${API_URL}/api/sell-quote`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              category, brand: 'Instant Estimate', model: query.trim(), storage: 'N/A', condition,
                              answers: { source: 'instant_estimate', is_low_data: true, not_in_catalog: result?.not_in_catalog || false },
                              delivery_method: 'pending', estimated_price: 0, quoted_price: 0, estimated_value: 0,
                              contact_name: contact.name, contact_email: contact.email, contact_phone: contact.phone,
                              sms_opt_in: !!smsOptIn,
                              sms_opt_in_text: smsOptIn ? SMS_OPT_IN_CONSENT_TEXT : null,
                            }),
                          });
                          if (!r.ok) throw new Error('submit failed');
                          setPhase('low-data-submitted');
                        } catch { alert('Something went wrong. Please try again.'); }
                        finally { setSubmitting(false); }
                      }}
                      disabled={!contact.name || !contact.email || !contact.phone || submitting}
                      className={`flex-1 ie-btn-amber py-2.5 text-sm ${(!contact.name || !contact.email || !contact.phone || submitting) ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid="low-data-submit-btn"
                    >
                      {submitting ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> Sending...</> : <><Send className="w-3.5 h-3.5 inline mr-1" /> Proceed &amp; Get My Quote</>}
                    </button>
                    <button onClick={reset} className="ie-btn-ghost px-3 sm:px-4 py-2.5 text-sm" data-testid="try-another-low-data-btn">Try Another</button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <p className="text-sm font-bold text-emerald-700 mb-1" data-testid="low-data-success">Order Received!</p>
                  <p className="text-xs text-slate-500 mb-3">Our team will send a custom estimate to <span className="text-slate-800 font-medium">{contact.email}</span> ASAP.</p>
                  <button onClick={reset} className="ie-btn-ghost px-6 py-2 text-sm">Get Another Estimate</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Broken result */}
        {result && !result.error && result.is_broken && (
          <div className="px-3 sm:px-5 pb-5 relative z-10">
            <div className="ie-inner-card p-5 text-center">
              <Wrench className="w-8 h-8 text-red-400/60 mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900 mb-1">Broken Device</p>
              <p className="text-xs text-slate-400 mb-3">Needs in-person evaluation.</p>
              <a href="/contact" className="ie-btn-ghost px-6 py-2 text-sm inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Contact Us</a>
            </div>
          </div>
        )}

        {/* No estimate / error */}
        {result && !result.error && !result.is_broken && !result.low_data && !result.rejected && !result.rate_limited && result.offer_price === 0 && (
          <div className="px-3 sm:px-5 pb-5 relative z-10">
            <div className="ie-inner-card p-5 text-center">
              <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-slate-900 mb-1">No Estimate Available</p>
              <p className="text-xs text-slate-400 mb-3">Try a more specific search or contact us.</p>
              <a href="/contact" className="ie-btn-ghost px-6 py-2 text-sm inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Get Custom Quote</a>
            </div>
          </div>
        )}

        {result && result.error && (
          <div className="px-3 sm:px-5 pb-5 relative z-10">
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-center">
              <p className="text-sm text-red-400 font-medium">Hmm, that took longer than expected. Please try again.</p>
              <button
                onClick={() => handleGetEstimate()}
                className="ie-btn-primary mt-3 px-5 py-2 text-sm"
                data-testid="estimate-retry-btn"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
      {!(result && (result.low_data || result.offer_price > 0)) && contactSupport}
      <IEStyles />
      <GuideStyles />
    </div>
  );
}

// ─── Shared header for delivery/contact phases ───
function IEHeader({ result, query, onReset }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 pt-3 sm:pt-4 pb-2">
      <div className="hidden sm:block"><RobotFace scanning={false} size={44} /></div>
      <div className="block sm:hidden"><RobotFace scanning={false} size={32} /></div>
      <div className="flex-1 min-w-0">
        <h2 className="text-xs sm:text-base font-bold text-slate-900 leading-tight flex items-center gap-1.5">
          Instant Estimate <span className="ie-ai-badge text-[7px] sm:text-[8px]">AI</span>
        </h2>
        <p className="text-[9px] sm:text-[10px] text-slate-500 truncate flex items-center gap-1.5">
          <img src={getDeviceImage(result?.device_name || query, result?.category)} alt="" loading="lazy" className="w-4 h-4 rounded object-contain bg-slate-100 flex-shrink-0" />
          {query}
        </p>
      </div>
      <button onClick={onReset} className="ie-btn-ghost px-2 sm:px-2.5 py-1 sm:py-1.5 text-[10px] sm:text-xs" data-testid="new-estimate-btn">
        <RotateCcw className="w-3 h-3 inline mr-0.5 sm:mr-1" /> New
      </button>
    </div>
  );
}

// ─── All custom styles ───
function IEStyles() {
  return (
    <style>{`
      [data-testid="sell-spec-picker"] .spec-chip-row { flex-wrap: wrap; overflow: visible; mask-image: none; -webkit-mask-image: none; }
      @media (max-width: 640px) {
        [data-testid="instant-estimate"] .ie-input { font-size: 16px !important; min-height: 50px; }
        [data-testid="instant-estimate"] .ie-cond-btn { min-height: 60px; }
        [data-testid="instant-estimate"] .spec-chip { min-height: 46px; padding-left: 14px; padding-right: 14px; font-size: 14px; }
        [data-testid="instant-estimate"] .sg-model-btn { min-height: 58px; font-size: 14px; }
        [data-testid="instant-estimate"] .sg-year-chip { min-height: 40px; padding: 6px 14px; font-size: 13px; }
        [data-testid="instant-estimate"] .sg-back-btn { min-height: 40px; }
      }
      @media (min-width: 768px) {
        [data-testid="instant-estimate"] .sg-model-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
        [data-testid="instant-estimate"] .sg-brand-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
        [data-testid="estimate-result-section"] .ie-result-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
      }
      @media (min-width: 1024px) {
        [data-testid="instant-estimate"] .sg-model-grid { grid-template-columns: repeat(4, 1fr); }
        [data-testid="instant-estimate"] .sg-brand-grid { grid-template-columns: repeat(4, 1fr); }
      }
      [data-testid="sell-spec-picker"] .spec-chip { max-width: 100%; white-space: normal; overflow-wrap: anywhere; }
      [data-testid="sell-spec-picker"] .spec-pick-model { min-width: 0; overflow-wrap: anywhere; }
      [data-testid="sell-spec-picker"] .spec-status-row { flex-wrap: wrap; }
      body:has(#instant-estimate-section) .absolute.top-40.w-96 { max-width: 66.666%; }
      body:has(#instant-estimate-section) [class*="-right-24"][class*="bg-emerald-400/10"] { right: 0; max-width: 100%; }
      body:has(#instant-estimate-section) [class*="-right-40"][class*="bg-violet-500/"] { right: 0; max-width: 100%; }
      .ie-card {
        background: #ffffff;
        border: 1px solid rgba(15,23,42,0.08);
        border-radius: 1.25rem;
        box-shadow: 0 10px 40px -14px rgba(15,23,42,0.14), 0 2px 8px -3px rgba(15,23,42,0.06);
      }
      @media (min-width: 640px) {
        .ie-card { border-radius: 1.5rem; }
      }
      .ie-inner-card {
        background: #f8fafc;
        border: 1px solid rgba(15,23,42,0.08);
        border-radius: 0.75rem;
      }
      .ie-input {
        background: #f8fafc;
        border: 1.5px solid rgba(15,23,42,0.12);
        border-radius: 0.875rem;
        color: #0f172a;
        font-size: 0.875rem;
        transition: all 0.2s;
        outline: none;
      }
      .ie-input:focus {
        background: #ffffff;
        border-color: #10b981;
        box-shadow: 0 0 0 3px rgba(16,185,129,0.12);
      }
      .ie-input::placeholder { color: rgba(100,116,139,0.6); }
      .ie-ai-badge {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 2px 8px;
        border-radius: 6px;
        background: rgba(6,182,212,0.14);
        border: 1px solid rgba(6,182,212,0.4);
        font-size: 9px;
        font-weight: 800;
        color: #0e7490;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      .ie-recommended-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 6px;
        background: rgba(34,197,94,0.14);
        border: 1px solid rgba(34,197,94,0.4);
        font-size: 8px;
        font-weight: 800;
        color: #15803d;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .ie-spec-tag {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 4px 8px;
        border-radius: 8px;
        font-size: 10px;
        font-weight: 600;
        border: 1px solid;
        transition: all 0.3s ease;
      }
      @media (min-width: 640px) {
        .ie-spec-tag { padding: 3px 10px; font-size: 11px; gap: 4px; }
      }
      .ie-spec-green {
        background: rgba(16,185,129,0.1);
        border-color: rgba(16,185,129,0.3);
        color: #34d399;
      }
      .ie-spec-red {
        background: rgba(239,68,68,0.1);
        border-color: rgba(239,68,68,0.3);
        color: #f87171;
      }
      .ie-suggestion-chip {
        padding: 6px 12px;
        border-radius: 8px;
        border: 1px solid rgba(15,23,42,0.12);
        background: #f8fafc;
        color: #475569;
        font-size: 11px;
        font-weight: 500;
        transition: all 0.2s;
        white-space: nowrap;
        flex-shrink: 0;
      }
      @media (min-width: 640px) {
        .ie-suggestion-chip { padding: 4px 12px; font-size: 11px; }
      }
      .ie-suggestion-chip:hover {
        border-color: rgba(16,185,129,0.5);
        color: #059669;
      }
      .ie-suggestion-skeleton {
        display: inline-block;
        width: 120px;
        height: 28px;
        border-radius: 8px;
        background: linear-gradient(90deg, rgba(30,41,59,0.5) 0%, rgba(51,65,85,0.7) 50%, rgba(30,41,59,0.5) 100%);
        background-size: 200% 100%;
        animation: ie-skel-shimmer 1.2s ease-in-out infinite;
        flex-shrink: 0;
      }
      @keyframes ie-skel-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (max-width: 639px) {
        .ie-suggestion-skeleton { width: 45%; height: 32px; }
      }
      .ie-suggestions-scroll {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      @media (max-width: 639px) {
        .ie-suggestions-scroll {
          flex-wrap: wrap;
          gap: 6px;
        }
      }
      .ie-cond-btn {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 12px 4px;
        border-radius: 14px;
        border: 1.5px solid rgba(15,23,42,0.1);
        background: #ffffff;
        color: #64748b;
        box-shadow: 0 1px 3px rgba(15,23,42,0.04);
        transition: all 0.2s;
        min-height: 58px;
      }
      @media (min-width: 640px) {
        .ie-cond-btn { gap: 4px; padding: 12px 4px; min-height: auto; }
      }
      .ie-cond-btn:hover { border-color: rgba(16,185,129,0.4); box-shadow: 0 4px 12px -6px rgba(15,23,42,0.15); }
      .ie-cond-active { color: #0f172a; }
      .ie-offer-card {
        position: relative;
        border-radius: 1rem;
        overflow: hidden;
        background: linear-gradient(135deg, #f0fdf4, #ecfeff);
        border: 2px solid transparent;
        background-clip: padding-box;
        box-shadow: 0 20px 50px -22px rgba(16,185,129,0.4);
      }
      .ie-offer-card::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 1.1rem;
        background: linear-gradient(135deg, #10b981, #06b6d4, #34d399, #06b6d4);
        background-size: 300% 300%;
        animation: ie-gradient-spin 5s ease infinite;
        z-index: -1;
      }
      @keyframes ie-gradient-spin {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .ie-offer-price {
        color: #0f172a;
        text-shadow: none;
      }
      .ie-btn-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-weight: 700;
        color: white;
        border-radius: 0.75rem;
        background: linear-gradient(135deg, #10b981, #06b6d4);
        transition: all 0.2s;
        box-shadow: 0 0 24px -2px rgba(16,185,129,0.35);
      }
      .ie-btn-primary:hover { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 0 34px -2px rgba(16,185,129,0.5); }
      .ie-btn-amber {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-weight: 700;
        color: white;
        border-radius: 0.75rem;
        background: linear-gradient(135deg, #f59e0b, #ea580c);
        transition: all 0.2s;
        box-shadow: 0 4px 15px -3px rgba(245,158,11,0.3);
      }
      .ie-btn-amber:hover { filter: brightness(1.1); }
      .ie-btn-ghost {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-weight: 500;
        color: rgba(148,163,184,1);
        border-radius: 0.75rem;
        border: 1px solid rgba(51,65,85,0.7);
        background: transparent;
        transition: all 0.2s;
      }
      .ie-btn-ghost:hover { background: rgba(30,41,59,0.5); }
      .ie-btn-submit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-weight: 700;
        color: white;
        border-radius: 0.75rem;
        background: linear-gradient(135deg, #10b981, #059669);
        transition: all 0.2s;
        box-shadow: 0 4px 15px -3px rgba(16,185,129,0.3);
      }
      .ie-btn-submit:hover:not(:disabled) { filter: brightness(1.1); }

      /* Info button pulse animation */
      .ie-info-pulse {
        animation: ie-pulse-ring 2s ease-out infinite;
      }
      @keyframes ie-pulse-ring {
        0% { box-shadow: 0 0 0 0 rgba(6,182,212,0.5); }
        70% { box-shadow: 0 0 0 6px rgba(6,182,212,0); }
        100% { box-shadow: 0 0 0 0 rgba(6,182,212,0); }
      }

      /* Fade in */
      .ie-fade-in {
        animation: ie-fade-in 1s ease-out;
      }
      @keyframes ie-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      /* Confetti animation */
      .ie-confetti-piece {
        animation: ie-confetti-fall 3s ease-out forwards;
      }
      @keyframes ie-confetti-fall {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(500px) rotate(720deg); opacity: 0; }
      }

      /* ───── MOBILE OVERRIDES (max-width: 640px) ───── */
      @media (max-width: 640px) {
        .ie-card { border-radius: 0.75rem; margin: 0 2px; }
        .ie-cond-btn { padding: 10px 6px; gap: 4px; border-radius: 10px; min-height: 56px; }
        .ie-suggestion-chip { padding: 8px 12px; font-size: 12px; white-space: normal; text-align: left; min-height: 36px; }
        .ie-spec-tag { padding: 4px 8px; font-size: 10px; }
        .ie-offer-card { border-radius: 0.875rem; }
        .ie-offer-card::before { border-radius: 1rem; }
        .ie-btn-primary, .ie-btn-amber, .ie-btn-ghost, .ie-btn-submit { border-radius: 0.625rem; font-size: 13px; min-height: 44px; }
        .ie-input { font-size: 16px; min-height: 44px; }
      }

      /* ───── TABLET OVERRIDES (641px - 1024px) ───── */
      @media (min-width: 641px) and (max-width: 1024px) {
        .ie-card { border-radius: 1rem; }
        .ie-cond-btn { padding: 12px 6px; gap: 4px; }
      }

      /* Success bounce */
      .ie-success-bounce {
        animation: ie-bounce-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes ie-bounce-in {
        0% { transform: scale(0); opacity: 0; }
        60% { transform: scale(1.2); }
        100% { transform: scale(1); opacity: 1; }
      }

      /* Success text shimmer */
      .ie-success-text {
        animation: ie-shimmer 2s ease-in-out infinite;
        background-size: 200% auto;
      }
      @keyframes ie-shimmer {
        0% { background-position: 0% center; }
        100% { background-position: 200% center; }
      }
    `}</style>
  );
}
